import { inject } from 'inversify';

import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Shift } from '@/entities/Shift';
import CONSTANTS from '@/helpers/constants';
import { Logger } from '@/services/Logger';
import { ShiftsReqMsg, ShiftUpdateReqMsg } from '@/services/signalr/MessageTypes/Shift';
import { Signalr } from '@/services/signalr/Signalr';
import { ShiftStore } from '@/stores/ShiftStore';
import { toIsoString } from '@/utils/toIsoString';
import type { IShiftInfo } from '~/api/IShiftInfo';
import type { IShiftUpdateRespMsg } from '~/api/messages/IShiftUpdateRespMsg';
import type { IShiftsRespMsg } from '~/api/messages/IShiftsRespMsg';

@SignalrListener
@service(ShiftManager)
export class ShiftManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(ShiftStore) protected shiftStore: ShiftStore;

  get all() {
    return this.shiftStore.all();
  }

  @SignalrMessage('ShiftUpdateRespMsg')
  async onShiftUpdateRespMsg(response: IShiftUpdateRespMsg) {
    if (!response.shift) return;
    await this.update([response.shift]);
  }

  @SignalrMessage('ShiftsRespMsg')
  async onShiftsRespMsg(response: IShiftsRespMsg) {
    if (!response.shifts?.length) return;
    await this.update(response.shifts);
  }

  find(id?: string) {
    return this.shiftStore.find(id);
  }

  async loadShifts(startDateTime: string, endDateTime: string): Promise<IShiftsRespMsg> {
    const response = await this.signalr.invoke(ShiftsReqMsg, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      contextKey: CONSTANTS.UI.CONTEXTS.SHIFT_UPDATE,
      startDateTime: toIsoString(startDateTime),
      endDateTime: toIsoString(endDateTime),
      openShiftsOnly: 'false',
    });
    await this.update(response?.shifts ?? []);
    return response;
  }

  async updateShift(shift: Partial<IShiftInfo>): Promise<IShiftUpdateRespMsg> {
    const normalized = new Shift({ ...shift });
    return this.signalr.invoke(ShiftUpdateReqMsg, {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      contextKey: CONSTANTS.UI.CONTEXTS.SHIFT_UPDATE,
      shift: normalized,
    });
  }

  async update(shifts: IShiftInfo[]) {
    this.shiftStore.update(...shifts.map((s) => new Shift(s)));
  }
}
